/**
 * What a COMPLETE board / list / task record looks like, in one place.
 *
 * `extension_records_upsert` replaces a record's `data` wholesale and never
 * merges it (`sync/records.rs:180-215`), so a field this file forgets is a
 * field that silently stops existing on every device. The app says the same
 * thing in its own words at `TaskBoardTab.js:1601-1626`: *"an undefined field
 * disappears through JSON — an assignee cleared to undefined would never clear
 * on any other device"*. Four concrete losses, all verified in the store:
 *
 * - omit `labels`   → the card's chips vanish and the filter bar can never
 *                     match it again;
 * - omit `comments` → every comment on that task is destroyed (they are nested
 *                     inside the task record, and this is a full replace);
 * - omit `position` → `compareTasksByPosition` reads it as 0 and the card jumps
 *                     to the top of its column on every client;
 * - omit `scope_id` → **the record becomes invisible**. `readBoardInScope`
 *                     filters on `id = ?N OR json_extract(data,'$.scope_id') = ?N`
 *                     and `patchBoardWithRecords` drops it too. The row stores,
 *                     it syncs, it never renders anywhere.
 *
 * ## Why the builders go through `decomposeBoard`
 *
 * `boardsStore.decomposeBoard` is the app's own decomposition and the only
 * definition of these shapes that the running app agrees with. Re-typing it
 * here would be a second implementation that drifts the first time a board
 * gains a field — the exact failure the list above describes. So each builder
 * hands `decomposeBoard` a one-entity board and takes the record back out.
 * `decomposeBoard` builds records, it does not write them: the write is the
 * caller's `extension_records_cas_upsert`, one record at a time. MCP never
 * touches the whole-scope path.
 *
 * ## Placement is `parent_id`, not `data.groupId`
 *
 * `assembleBoard`'s `placeTask` sets `t.groupId = r.parent_id` whenever
 * `parent_id !== board.id`, and its comment calls `data.groupId` *"only a
 * mirror … it goes stale the moment ids change"*. A task whose `parent_id` is
 * the board id and whose `groupId` names no existing list renders in **no
 * column at all** — present, synced, invisible. `decomposeBoard` derives
 * `parent_id` from `task.groupId`, so setting `groupId` correctly is what keeps
 * the two in agreement; every caller here must set it.
 *
 * ## Ordering
 *
 * `sort_key` carries no meaning for boards — `decomposeBoard` sends `null` and
 * `upsert_record` stores the literal `'V'`, so `ORDER BY sort_key, id`
 * degenerates to `ORDER BY id`. Ordering is `data.position`, an integer, with a
 * stable id tie-break (`compareGroupsByPosition` / `compareTasksByPosition`).
 * Two clients independently appending land on the SAME position and the id
 * tie-break resolves it — that is by design, so these helpers reproduce the
 * app's own "count what is already there" rule and do not try to be cleverer
 * (documents' fractional `keyBetween` is a documents mechanism).
 */
import { statusPatch } from '../extensions/bundled/task-manager/taskStatus';
import { decomposeBoard } from '../extensions/bundled/task-manager/boardsStore';
import { OUTBOX_ITEM_MAX_BYTES } from '../records/syncLimits';

/** The app's own id generator (`TaskBoardTab.js:87`). */
export function newId() {
  return crypto.randomUUID();
}

export function nowIso() {
  return new Date().toISOString();
}

/** The board head record, from a board-shaped object (no groups, no tasks). */
export function boardRecordFor(board) {
  return decomposeBoard({ ...board, groups: [], tasks: [] })[0];
}

/** One list record. `group` is `{ id, name, position }`. */
export function groupRecordFor(boardId, group) {
  return decomposeBoard({ id: boardId, groups: [group], tasks: [] })[1];
}

/** One task record. `task.groupId` decides `parent_id`. */
export function taskRecordFor(boardId, task) {
  return decomposeBoard({ id: boardId, groups: [], tasks: [task] })[1];
}

/**
 * A record built from an EXISTING row's `data` plus a patch — the
 * read-modify-write every update tool uses.
 *
 * Deliberately not routed through `decomposeBoard`: that enumerates the fields
 * it knows about, so a field the app gains before this file learns about it
 * would be dropped by a rebuild. Spreading the row's own data forward keeps
 * unknown keys, which is the safe direction for a full-replace store.
 */
export function patchedRecord(row, patch) {
  const { id, record_type: recordType, parent_id: parentId, sort_key: sortKey, ...data } = row;
  return {
    id,
    record_type: recordType,
    parent_id: patch?.parent_id !== undefined ? patch.parent_id : parentId ?? null,
    // `null` here, exactly as decomposeBoard sends for every board record —
    // upsert_record then stores the literal 'V' both devices agree on.
    sort_key: null,
    data: { ...data, ...(patch?.data || {}) },
  };
}

/** A record's `data`, i.e. the flat row minus the four envelope columns. */
export function dataOf(row) {
  const { id, record_type: recordType, parent_id: parentId, sort_key: sortKey, ...data } = row;
  return data;
}

/** A new task with EVERY field the app writes, explicitly (see the header). */
export function newTaskFields({
  id,
  groupId,
  position,
  title,
  descriptionHtml = '',
  priority = 'medium',
  labels = [],
  dueDate = null,
  done,
  status,
  subtasks = [],
  now = nowIso(),
}) {
  const task = {
    id,
    groupId,
    position,
    title,
    descriptionHtml,
    priority,
    // Explicit null, never undefined — see the header. MCP cannot set either
    // of these: naming a person means either accepting a raw user id or
    // resolving a name to one, and the user directory is deliberately not
    // browsable in this product. Tasks arrive unassigned; a human assigns them.
    assigneeId: null,
    ownerId: null,
    labels,
    dueDate,
    createdAt: now,
    updatedAt: now,
    // Explicit empty array. Comments live INSIDE the task record, so a task
    // written without this key has no comments rather than an empty list, and
    // the next full-replace write of it would be indistinguishable from one
    // that deleted them.
    comments: [],
    // The checklist, already validated by the caller (`subtasksFromInput`).
    // Explicit `[]` like `comments`, so a new card always carries the key.
    subtasks,
  };
  // `done` is genuinely optional in the app (`showDoneTasks || !t.done`), so it
  // is only written when asked for rather than defaulted to a value the app
  // never writes on create.
  if (done !== undefined) task.done = !!done;
  // A status (To do / In progress / Done / Dismissed, card ef074711) is
  // written together with the `done` it implies — the app's own rule.
  if (status !== undefined) Object.assign(task, statusPatch(status));
  // Created closed: closed now (the done date the card shows).
  if (task.done === true) task.doneAt = now;
  return task;
}

/** Position for a new list: the app's own `prev.groups.length`. */
export function nextGroupPosition(groups) {
  return (groups || []).length;
}

/** Position for a new card: the app's own count of that list's tasks. */
export function nextTaskPosition(tasks, listId) {
  return (tasks || []).filter((t) => (t.parent_id ?? t.groupId) === listId).length;
}

/**
 * Below this, a record's `data` is comfortably inside the outbox cap
 * (`OUTBOX_ITEM_MAX_BYTES`, 250 000 since 14.10.2026) that
 * `extension_records_cas_upsert` pre-checks. A DIAGNOSTIC, not enforcement:
 * `board_read` reports `syncable: false` per task so an agent can see a card
 * that has already stopped syncing (the app itself has no such signal — a human
 * typing a long description crosses the cap and gets a log line and nothing
 * else, which is a separate piece of work). The real check is Rust's, on the
 * serialised outbox op, which is strictly larger than `data` alone; the margin
 * here is what keeps this from claiming a record is fine when the op is not.
 */
const RECORD_SIZE_HINT = OUTBOX_ITEM_MAX_BYTES - 2000;

export function isOverSyncHint(data) {
  try {
    return JSON.stringify(data ?? {}).length > RECORD_SIZE_HINT;
  } catch {
    return false;
  }
}

/**
 * The flat wire shape `patchBoardWithRecords` consumes, i.e. exactly what
 * Rust's `record_json` returns: the envelope columns spread over `data`.
 * Used to hand the OPEN board tab the record we just wrote, before the nonce
 * bump — see `uiRefresh.js` for why that order is not optional.
 */
export function toWireRecord(record) {
  return {
    ...record.data,
    id: record.id,
    record_type: record.record_type,
    parent_id: record.parent_id ?? null,
    sort_key: record.sort_key ?? 'V',
  };
}
